import { useEffect, useState } from 'react';
import { hosted, offline, type AllConsentNames } from 'c15t';
import { createConsentRuntime, type ConsentRuntime } from 'c15t/runtime';
import {
  buildGoogleConsentScripts,
  CONSENT_CATEGORIES,
  CONSENT_DISABLED_POLICY,
} from '@belong/common';

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-T84J732';
const GTM_VIEW = process.env.NEXT_PUBLIC_GTM_VIEW;
const IS_PRODUCTION = process.env.NEXT_PUBLIC_ENV === 'production';
const CONSENT_DISABLED = process.env.NEXT_PUBLIC_C15T_DISABLED === '1';

let runtime: ConsentRuntime | undefined;

export function startConsent(): void {
  runtime = createConsentRuntime({
    mode: CONSENT_DISABLED
      ? offline({ policyRules: [CONSENT_DISABLED_POLICY] })
      : hosted({ backendURL: '/api/c15t' }),
    consentCategories: [...CONSENT_CATEGORIES],
    scripts: buildGoogleConsentScripts({
      gtmId: IS_PRODUCTION || GTM_VIEW ? GTM_ID : undefined,
      gtmView: GTM_VIEW,
      ga4Id: process.env.NEXT_PUBLIC_GA4_ID,
      getModel: () => runtime?.kernel.getSnapshot().model,
      onError: (mode, error) =>
        console.warn(`Failed to send the Consent Mode ${mode}`, error),
    }),
  });
  runtime.start();
}

export function hasConsent(category: AllConsentNames): boolean {
  return runtime?.kernel.getSnapshot().effectivePermissions[category] ?? false;
}

const subscribe = (listener: () => void) =>
  runtime?.kernel.subscribe(listener) ?? (() => undefined);

export function useConsent(category: AllConsentNames): boolean {
  const [granted, setGranted] = useState(() => hasConsent(category));

  useEffect(
    () => subscribe(() => setGranted(hasConsent(category))),
    [category],
  );

  return granted;
}

export function onConsentChange(
  category: AllConsentNames,
  listener: (granted: boolean) => void,
): void {
  let last: boolean | undefined;
  const check = () => {
    if (runtime?.kernel.getSnapshot().policyPending) {
      return;
    }

    const granted = hasConsent(category);

    if (granted !== last) {
      last = granted;
      listener(granted);
    }
  };

  check();
  subscribe(check);
}
